import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useStorageUpload, uploadedFileFields } from '../../lib/upload'
import { formatDate } from '../../lib/format'
import type { DailySafetyReport as DailySafetyReportRow } from '../../types'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, EmptyState, Input, Select,
  Skeleton, Textarea,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, AlertIcon, PlusIcon } from '../../components/icons'

interface ReportsResponse {
  ok: boolean; items: DailySafetyReportRow[]
  shifts: string[]; ratings: string[]; checklist_items: [string, string][]
}

const FALLBACK_SHIFTS = ['Day', 'Night', 'Full day']
const FALLBACK_RATINGS = ['Safe', 'Minor concerns', 'Action required', 'Unsafe — stop work']
const FALLBACK_CHECKLIST: [string, string][] = [
  ['ppe_worn', 'PPE worn by all personnel'],
  ['ground_support', 'Ground / slope support adequate'],
  ['ventilation', 'Ventilation adequate'],
  ['equipment_safe', 'Equipment & machinery safe and guarded'],
  ['explosives_secure', 'Explosives stored & handled correctly'],
  ['electrical_safe', 'Electrical installations safe'],
  ['emergency_exits', 'Emergency exits / escape ways clear'],
  ['first_aid', 'First-aid kit & trained responder available'],
  ['water_management', 'Water / flooding under control'],
  ['housekeeping', 'Housekeeping & access ways clear'],
]

export default function DailySafetyReport() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const upload = useStorageUpload(`daily_safety_reports/${user?.org_id ?? ''}`)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'daily-safety-reports'],
    queryFn: () => api.get<ReportsResponse>('/ops/daily-safety-reports'),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.offline.post('/ops/daily-safety-reports', body, 'Daily safety report'),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['ops', 'daily-safety-reports'] }); setShowForm(false) },
  })

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const checklist: Record<string, string> = {}
    for (const [key] of checklistItems) checklist[key] = String(form.get(`chk_${key}`) ?? 'ok')

    const file = fileRef.current?.files?.[0]
    let fileFields: Record<string, string> = {}
    if (file) {
      try {
        fileFields = uploadedFileFields(await upload.upload(file), 'supporting_document')
      } catch {
        return
      }
    }

    create.mutate({
      report_date: form.get('report_date'),
      shift: form.get('shift'),
      personnel_on_site: Number(form.get('personnel_on_site') ?? 0),
      overall_rating: form.get('overall_rating'),
      checklist,
      toolbox_talk_held: form.get('toolbox_talk_held') === 'on',
      toolbox_talk_topic: form.get('toolbox_talk_topic'),
      incidents_today: Number(form.get('incidents_today') ?? 0),
      hazards_identified: Number(form.get('hazards_identified') ?? 0),
      observations: form.get('observations'),
      corrective_actions: form.get('corrective_actions'),
      ...fileFields,
    })
  }

  const items = data?.items ?? []
  const shifts = data?.shifts?.length ? data.shifts : FALLBACK_SHIFTS
  const ratings = data?.ratings?.length ? data.ratings : FALLBACK_RATINGS
  const checklistItems = data?.checklist_items?.length ? data.checklist_items : FALLBACK_CHECKLIST

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Daily safety report</h1>
          <p className="page-head__sub">Per-shift safety sign-off: checklist, headcount and observations.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'File report'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'File report'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="File a daily safety report" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack">
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not file that report.'}
                  </Alert>
                )}
                {upload.error && <Alert tone="danger">{upload.error}</Alert>}
                {/* PWA: 4 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Shift', content: (
                <div className="grid grid--2">
                  <Input label="Report date" name="report_date" type="date" required
                         defaultValue={new Date().toISOString().slice(0, 10)} />
                  <Select label="Shift" name="shift" defaultValue={shifts[0]}>
                    {shifts.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </div>
                    ) },
                    { title: 'Site status', content: (
                <div className="grid grid--2">
                  <Input label="Personnel on site" name="personnel_on_site" type="number" min="0" defaultValue={0} />
                  <Select label="Overall rating" name="overall_rating" defaultValue="Safe">
                    {ratings.map((r) => <option key={r} value={r}>{r}</option>)}
                  </Select>
                </div>
                    ) },
                    { title: 'Checklist', content: (
                <div className="field">
                  <label className="field__label">Safety checklist</label>
                  <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
                    {checklistItems.map(([key, label]) => (
                      <div key={key} className="row" style={{ justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
                        <span style={{ fontSize: 'var(--text-sm)' }}>{label}</span>
                        <select name={`chk_${key}`} className="select" defaultValue="ok" style={{ width: 120 }}>
                          <option value="ok">OK</option>
                          <option value="issue">Issue</option>
                          <option value="na">N/A</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
                    ) },
                    { title: 'Toolbox & counts', content: (
                      <div className="stack">
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', fontSize: 'var(--text-sm)' }}>
                  <input type="checkbox" name="toolbox_talk_held" /> Toolbox talk held
                </label>
                <Input label="Toolbox talk topic" name="toolbox_talk_topic" />

                <div className="grid grid--2">
                  <Input label="Incidents today" name="incidents_today" type="number" min="0" defaultValue={0} />
                  <Input label="Hazards identified" name="hazards_identified" type="number" min="0" defaultValue={0} />
                </div>
                      </div>
                    ) },
                    { title: 'Observations & evidence', content: (
                      <div className="stack">
                <Textarea label="Observations" name="observations" />
                <Textarea label="Corrective actions" name="corrective_actions" />
                <Input label="Supporting document (optional)" type="file" ref={fileRef} accept="image/*,application/pdf" />
                {upload.progress !== null && <div className="field__hint">Uploading… {upload.progress}%</div>}
                      </div>
                    ) },
                  ]}
                  footer={(
                <div className="row">
                  <Button type="submit" loading={create.isPending || upload.progress !== null}>File report</Button>
                  <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Your reports" />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} w="70%" /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<AlertIcon size={22} />} title="No reports yet" body="Filed daily safety reports will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Date</th><th>Shift</th><th>Rating</th><th className="table__num">Personnel</th><th className="table__num">Issues</th></tr></thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id}>
                      <td>{formatDate(r.report_date)}</td>
                      <td>{r.shift || '—'}</td>
                      <td>
                        <Badge dot tone={
                          r.overall_rating?.includes('Unsafe') ? 'danger'
                          : r.overall_rating?.includes('Action') ? 'warning'
                          : r.overall_rating?.includes('Minor') ? 'warning'
                          : 'success'
                        }>
                          {r.overall_rating || 'Safe'}
                        </Badge>
                      </td>
                      <td className="table__num">{r.personnel_on_site ?? 0}</td>
                      <td className="table__num">{r.issue_count ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
